import React, { useState } from "react";
import {
  ReservationColumnKey,
  SettlementDataTable,
} from "../components/SettlementDataTable";
import MultiSelect from "@/components/common/MultiSelect";
import { Button } from "@/components/ui/button";
import {
  Briefcase,
  Download,
  Luggage,
  MessageSquare,
  PlaneTakeoff,
  SquareUserRound,
  UserPlus,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { exportSettlementBookingsToCSV } from "@/components/pages/settlement/utils/exportSettlementBookingsToCSV";
import {
  ServiceRequestStatus,
  useDepartures,
} from "@/components/pages/settlement/hooks/useSettlement";
import { useDeparturesSummary } from "@/components/pages/settlement/hooks/useSettlementSummary";
import { DatePicker } from "@/components/common/DatePicker";
import { useHotel } from "@/hooks/useHotel";
import { useGuestNationalities } from "@/components/pages/reservations/guests/hooks/useGuests";
import * as countries from "i18n-iso-countries";
import { useBookingSources } from "@/components/pages/settlement/hooks/useBookingSources";
import { PreCheckStatus } from "@/components/pages/settlement/types/settlementBooking";
import { format, startOfDay } from "date-fns";
import { useSettlementSelectedOptions } from "../hooks/useSettlementSelectedOptions";
import { InteractiveStatusCell } from "../components/table-cells/InteractiveStatusCell";
import { SettlementFiltersBar } from "../components/SettlementFiltersBar";
import { CheckoutStatus } from "@/types/booking";
import { SettlementSummaryBar } from "@/components/pages/settlement/components/SettlementSummaryBar";
import { StatCard } from "@/components/pages/settlement/components/StatCard";
import { getSafeValue } from "@/components/pages/settlement/utils/getSafeValue";
import { useTranslation } from "react-i18next";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

const departuresColumns: ReservationColumnKey[] = [
  "select",
  "mainGuest",
  "guests",
  "room",
  "arrivalDate",
  "departureDate",
  "preCheckOut",
  "requests",
  "balance",
  "reviewsCount",
  "checkOutCompleted",
  "actions",
];

const perPage = 10;

export const DeparturesPage = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [departureDate, setDepartureDate] = useState<Date>(
    startOfDay(new Date()),
  );
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null,
  );
  const [preCheckoutStatus, setPreCheckoutStatus] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [requestsStatus, setRequestsStatus] = useState<ServiceRequestStatus[]>(
    [],
  );
  const [nationality, setNationality] = useState<string[]>([]);

  const { hotelId } = useHotel();
  const { capabilities } = useAccessCapabilities();

  const { reservations, loading, totalPages } = useDepartures(
    {
      currentPage,
      perPage,
      departureDate: format(departureDate, "yyyy-MM-dd"),
      sortField,
      sortDirection,
      preCheckoutStatus,
      channels,
      requestsStatus: capabilities.canUseRequestsSystem ? requestsStatus : [],
      nationality,
    },
    hotelId,
  );
  const { data: summary } = useDeparturesSummary(hotelId);

  const { handleChangeStatus, ...rest } = useSettlementSelectedOptions({
    reservations,
    mode: "checkout",
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => {
        if (prev === "asc") return "desc";
        if (prev === "desc") return null;
        return "asc";
      });
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const { data: bookingSources } = useBookingSources(hotelId);
  const { data: nationalitiesData } = useGuestNationalities(hotelId);

  const nationalityOptions = (nationalitiesData || []).map((countryCode) => ({
    value: countryCode,
    label: countries.getName(countryCode, "en") || countryCode,
  }));

  const hasSelection = rest?.selectedCount > 0;
  const visibleColumns = capabilities.canUseRequestsSystem
    ? departuresColumns
    : departuresColumns.filter((column) => column !== "requests");

  const handleCSVExport = () => {
    const selectedIds = rest.selectedItems;
    const dataToExport =
      selectedIds.length > 0
        ? reservations.filter((r) => selectedIds.includes(r.id))
        : reservations;

    exportSettlementBookingsToCSV(
      dataToExport,
      visibleColumns,
      t,
      "departures-export.csv",
    );
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-4 p-12">
      <SettlementSummaryBar>
        <StatCard
          icon={<Briefcase className="text-light-stone size-6" />}
          label={t("reservation.statusCard.totalDepartures")}
          value={getSafeValue(summary?.total)}
        />
        <StatCard
          icon={<Luggage className="text-light-stone size-6" />}
          label={t("reservation.statusCard.preCheckouts")}
          value={getSafeValue(summary?.preCheckOutsPercentage)}
        />
        {capabilities.canUseRequestsSystem ? (
          <StatCard
            icon={<PlaneTakeoff className="text-light-stone size-6" />}
            label={t("reservation.statusCard.activeDeparturesRequests")}
            value={getSafeValue(summary?.serviceRequestsActiveCount)}
          />
        ) : null}
      </SettlementSummaryBar>

      <div className={"flex flex-col gap-6"}>
        <div
          className={cn(
            "flex items-center justify-between",
            hasSelection && "bg-neutral-50 px-6 py-4",
          )}
        >
          {hasSelection ? (
            <span className="text-sm font-medium text-neutral-400">
              {rest.selectedCount} selected
            </span>
          ) : (
            <SettlementFiltersBar>
              <DatePicker
                placeholder={t("reservation.settlement.departureDate")}
                date={departureDate}
                onDateChange={setDepartureDate}
                minDate={new Date()}
                className="text-foreground flex h-8 min-h-8 items-center gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
              />
              <MultiSelect
                options={[
                  { label: "Done", value: PreCheckStatus.DONE },
                  { label: "Not done", value: PreCheckStatus.NOT_DONE },
                  { label: "Sent", value: PreCheckStatus.SENT },
                ]}
                placeholder={t("reservation.settlement.preCheckOut")}
                value={preCheckoutStatus}
                onValueChange={setPreCheckoutStatus}
                className="text-foreground min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs aria-expanded:border-b-0"
                dropdownClassName="border border-slate-200 border-t-0"
              />
              {!!bookingSources?.length && (
                <MultiSelect
                  className={
                    "text-foreground min-h-8 min-w-[110px] gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs aria-expanded:border-b-0"
                  }
                  options={
                    bookingSources.map((bs) => ({
                      label: bs.toLowerCase(),
                      value: bs,
                    })) ?? []
                  }
                  placeholder={"Chanel"}
                  value={channels}
                  onValueChange={setChannels}
                  dropdownClassName="border border-slate-200 border-t-0"
                />
              )}
              {!!nationalityOptions.length && (
                <MultiSelect
                  options={nationalityOptions}
                  placeholder={"Nationality"}
                  value={nationality}
                  onValueChange={setNationality}
                  className={
                    "text-foreground min-h-8 min-w-[110px] gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs aria-expanded:border-b-0"
                  }
                  dropdownClassName="border border-slate-200 border-t-0"
                />
              )}
              {capabilities.canUseRequestsSystem ? (
                <MultiSelect
                  className={
                    "text-foreground min-h-8 min-w-[110px] gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs aria-expanded:border-b-0"
                  }
                  options={[
                    {
                      label: "Completed",
                      value: ServiceRequestStatus.COMPLETED,
                    },
                    {
                      label: "Not Completed",
                      value: ServiceRequestStatus.IN_PROGRESS,
                    },
                  ]}
                  placeholder={"Requests"}
                  value={requestsStatus}
                  onValueChange={setRequestsStatus as any}
                  dropdownClassName="border border-slate-200 border-t-0"
                />
              ) : null}
            </SettlementFiltersBar>
          )}
          <div className="flex items-center gap-x-2">
            {hasSelection && (
              <>
                <InteractiveStatusCell
                  statuses={[
                    {
                      status: CheckoutStatus.DONE,
                      color: "border-accent-green text-accent-green",
                      text: "Confirm",
                    },
                    {
                      status: CheckoutStatus.NOT_DONE,
                      color: "border-gray-5 text-gray-5",
                      text: "Check-out",
                    },
                  ]}
                  currentStatus={CheckoutStatus.NOT_DONE}
                  onStatusChange={(newStatus) =>
                    handleChangeStatus(newStatus as CheckoutStatus)
                  }
                  className="text-foreground h-8 flex-row-reverse rounded-lg border border-slate-200 bg-white px-3 py-1 text-sm font-medium hover:opacity-100"
                  icon={<SquareUserRound className="size-4" />}
                />

                <Button
                  variant="icon-text"
                  className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
                >
                  <MessageSquare className="size-4" />
                  {t("reservation.buttons.sendMessage")}
                </Button>

                <Button
                  variant="icon-text"
                  className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
                >
                  <UserPlus className="size-4" />
                  {t("reservation.buttons.sendConfirmation")}
                </Button>
              </>
            )}
            <Button
              variant="icon-text"
              onClick={handleCSVExport}
              className="text-foreground h-8 min-h-8 gap-1 border border-slate-200 py-1 text-sm font-medium shadow-xs"
            >
              <Download className="size-4" />
              {t("reservation.buttons.export")}
            </Button>

            {hasSelection && (
              <Button
                variant="icon-text"
                onClick={rest.clearSelection}
                className="h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium text-red-500 shadow-xs"
              >
                <X className="size-4" />
                {t("reservation.buttons.cancel")}
              </Button>
            )}
          </div>
        </div>

        <SettlementDataTable
          data={reservations}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          loading={loading}
          showEditAction={false}
          {...rest}
        />
      </div>
    </div>
  );
};
